import {View} from "react-native";
import {EditParticipantRequest, EventHappening, EventRoles, Participating} from "@/lib/types";
import {useLocalSearchParams, useRouter} from "expo-router";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import Page from "@/lib/components/Page";
import ErrorPage from "@/lib/components/ErrorPage";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import SpinnerPage from "@/lib/components/SpinnerPage";
import Header from "@/lib/components/Header";
import Badge from "@/lib/components/Badge";
import {useEffect, useState} from "react";
import Radio from "@/lib/components/Radio";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import ErrorMessage from "@/lib/components/ErrorMessage";

export default function RoleEditPage() {
    const {event_id, username} = useLocalSearchParams();
    const {session} = useSession();
    const [roles, setRoles] = useState<EventRoles | null>(null);
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const queryClient = useQueryClient();
    const router = useRouter();

    const eventQuery = useQuery<EventHappening>({
        queryKey: ['event', event_id, session],
        queryFn: () => fetchBackend(`/event/${event_id}`, "GET", session),
        retry: dataRetryPolicy,
        staleTime: 1000 * 60 * 15,
    })

    const subject =
        (
            eventQuery.data ?
                eventQuery.data.participants.find(p => p.avatar.username === username) :
                undefined
        );

    useEffect(() => {
        if (!subject) {
            return;
        }
        setRoles({
            is_host: subject.status === Participating.HOST,
            is_moderator: subject.status === Participating.MODERATOR,
            is_blocked: subject.status === Participating.BLOCKED,
            can_invite: subject.can_invite,
            can_post: subject.can_post,
        });
    }, [subject])

    if (eventQuery.error) {
        return <ErrorPage error={eventQuery.error}/>
    } else if (eventQuery.isPending || !eventQuery.data || !roles) {
        return <SpinnerPage/>
    }

    if (!subject) {
        const msg = `Bruker med navn "${username}" er ikke registrert på eventet.`
        return <ErrorPage error={new Error(msg)}/>
    }

    async function saveRoles() {
        if (!eventQuery || !eventQuery.data || !subject || !roles) {
            return;
        }
        setSaveError(null);
        setBusy(true);
        let payload: EditParticipantRequest = {
            user_id: subject.avatar.id,
            roles,
        }
        fetchBackendNoContent(`/event/${eventQuery.data.event_id}/participant`, "PUT", session, payload).then(async () => {
            await queryClient.invalidateQueries({queryKey: ['event', event_id, session]});
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    return (
        <Page scrollable={true}>
            <View style={{gap: 24}}>
                <View style={{gap: 8}}>
                    <Header title={subject.avatar.username} icon={"edit"}/>

                    <View style={{alignItems: "flex-start"}}>
                        <Badge title={eventQuery.data.title}
                               color={eventQuery.data.color}
                               contrastColor={eventQuery.data.contrast_color}
                        />
                    </View>
                </View>

                <View style={{gap: 8}}>
                    <Header title={"Angi rolle"}/>
                    <Radio selected={roles.is_host}
                           onPress={() => setRoles({...roles, is_host: true, is_moderator: false})}
                           title={"Arrangør"}
                           description={"Har alle rettigheter og kan i tillegg bestemme roller."}
                           checkbox={false}
                    />
                    <Radio selected={roles.is_moderator}
                           onPress={() => setRoles({...roles, is_host: false, is_moderator: true})}
                           title={"Moderator"}
                           description={"Kan invitere og utestenge, starte diskusjoner, samt redigere eventet."}
                           checkbox={false}
                    />
                    <Radio selected={!roles.is_host && !roles.is_moderator}
                           onPress={() => setRoles({...roles, is_host: false, is_moderator: false})}
                           title={"Deltaker"}
                           description={"Ingen ekstra rettigheter utover eventets innstillinger."}
                           checkbox={false}
                    />
                </View>

                <View style={{gap: 8}}>
                    <Header title={"Ekstra tilganger"}/>
                    <Radio selected={roles.can_invite}
                           onPress={() => setRoles({...roles, can_invite: !roles.can_invite})}
                           title={"Invitasjon"}
                           description={"Deltakeren kan invitere venner til eventet"}
                           checkbox={true}
                    />
                    <Radio selected={roles.can_post}
                           onPress={() => setRoles({...roles, can_post: !roles.can_post})}
                           title={"Chat"}
                           description={"Deltakeren kan starte diskusjonstråder"}
                           checkbox={true}
                    />
                </View>

                {saveError && <ErrorMessage error={saveError}/>}

                <Nav>
                    <ActionButton onPress={saveRoles} icon={"checkmark"} title={"Lagre"} busy={busy}/>
                </Nav>
            </View>
        </Page>
    )
}